import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";

import type { MetricKey, Scope } from "@busright/data-contract";

import { DatasetDrawer } from "@/components/DatasetDrawer";

// =================================================================================================
// DrillContext — the single entry point to "every number is drillable".
//
// One `DatasetDrawer` is mounted at the app root. Anything in the tree calls `openDrill(metricKey,
// scope)` (via the `Drillable` wrapper or directly) to open it for that metric+scope. The drawer
// itself owns the in-drawer breadcrumb stack (drilling a row pushes a deeper frame); this context
// only owns the OUTERMOST open frame and whether the drawer is open. Closing clears it.
// =================================================================================================

export interface DrillFrame {
  readonly metricKey: MetricKey;
  readonly scope: Scope;
}

export interface DrillContextValue {
  // Open the drawer at the given metric + scope (the root of a fresh breadcrumb stack).
  readonly openDrill: (metricKey: MetricKey, scope: Scope) => void;
  // Close the drawer (clears the stack).
  readonly closeDrill: () => void;
  readonly isOpen: boolean;
}

const DrillContext = createContext<DrillContextValue | null>(null);

export const DrillProvider = ({ children }: { readonly children: ReactNode }): ReactNode => {
  const [frame, setFrame] = useState<DrillFrame | null>(null);

  const openDrill = useCallback((metricKey: MetricKey, scope: Scope): void => {
    setFrame({ metricKey, scope });
  }, []);

  const closeDrill = useCallback((): void => setFrame(null), []);

  const value = useMemo<DrillContextValue>(
    () => ({ openDrill, closeDrill, isOpen: frame !== null }),
    [openDrill, closeDrill, frame],
  );

  return (
    <DrillContext.Provider value={value}>
      {children}
      <DatasetDrawer root={frame} open={frame !== null} onClose={closeDrill} />
    </DrillContext.Provider>
  );
};

// Open/close the dataset drawer. Throws outside a provider so a missing mount is a loud bug.
export const useDrill = (): DrillContextValue => {
  const ctx = useContext(DrillContext);
  if (ctx === null) {
    throw new Error("useDrill must be used within a <DrillProvider>");
  }
  return ctx;
};
